/**
 * Minimal fetch wrapper for the mob PWA. Talks to the same Worker API as the
 * main dashboard. Auth via the cross-subdomain `nightshade_session` cookie
 * (set at `.nightshade.gameonsolutions.uk` by the dashboard's /auth/login).
 *
 * Auth flow: user signs in once on the main dashboard. The cookie is then
 * shared by every subdomain. Mob never sees the JWT — `credentials: include`
 * does the work. On 401 we redirect to dashboard /login with `?return=…`.
 */
export const apiBase =
  (import.meta.env.VITE_API_BASE as string | undefined)
    ?? 'https://api.nightshade.gameonsolutions.uk';

const DASHBOARD_LOGIN = 'https://nightshade.gameonsolutions.uk/login';

/** Redirect to the dashboard login with a `?return=<currentPage>` so the
 *  user lands back on mob after they sign in. */
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  const ret = encodeURIComponent(window.location.href);
  window.location.href = `${DASHBOARD_LOGIN}?return=${ret}`;
}

class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function call<T>(
  method: string, path: string, body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
  const r = await fetch(`${apiBase}${path}`, init);
  const text = await r.text();
  if (!r.ok) {
    if (r.status === 401) {
      // No cookie / expired — bounce to dashboard login
      redirectToLogin();
    }
    throw new ApiError(r.status, text.slice(0, 500), `HTTP ${r.status}: ${text.slice(0, 200)}`);
  }
  if (!text) return undefined as T;
  try { return JSON.parse(text) as T; }
  catch { return text as unknown as T; }
}

export const api = {
  get:    <T>(p: string) => call<T>('GET',    p),
  post:   <T>(p: string, body?: unknown) => call<T>('POST',   p, body ?? {}),
  patch:  <T>(p: string, body?: unknown) => call<T>('PATCH',  p, body ?? {}),
  delete: <T>(p: string) => call<T>('DELETE', p),
};

// ---- Domain types (mirror Worker /research routes) ----

export interface InboxRow {
  id: string;
  url: string;
  title: string | null;
  ogImage: string | null;
  ogDescription: string | null;
  ogSitename: string | null;
  state: 'inbox' | 'archived' | 'assigned';
  assignedProductionId: string | null;
  assignedSourceId: string | null;
  sourceKind: string;
  note: string | null;
  createdAt: number;
}

export interface QuoteRow {
  id: string;
  inboxId: string;
  quoteText: string;
  note: string | null;
  anchor: string | null;
  createdAt: number;
}

export interface Production {
  id: string;
  topic: string;
  channelId: string;
  state: string;
}

export interface Channel {
  id: string;
  name: string;
}
